import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, Check, X, Pencil, Search, Settings as SettingsIcon, Save } from "lucide-react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ui, SECTION_TAG_LABELS, formatDate } from "../lib/ui";
import { useNavigate } from "react-router-dom";

const TABS = [
  { key: "pending", label: "Pending", testid: "admin-tab-pending" },
  { key: "approved", label: "Approved", testid: "admin-tab-approved" },
  { key: "rejected", label: "Rejected", testid: "admin-tab-rejected" },
];

function Field({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#5A4441]">{label}</div>
      <div className="text-[#210201] whitespace-pre-wrap">{String(value)}</div>
    </div>
  );
}

function ReportDetail({ report, onClose, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(report);
  const [rejectNote, setRejectNote] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (path, value) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) { if (ref[keys[i]] == null) ref[keys[i]] = {}; ref = ref[keys[i]]; }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const saveEdits = async () => {
    setBusy(true);
    try {
      const body = {
        exam_date: form.exam_date, city: form.city, country: form.country,
        centre_name: form.centre_name, note: form.note, difficulty: form.difficulty,
        eo: form.eo, ee: form.ee, co: form.co, ce: form.ce,
      };
      const { data } = await api.put(`/admin/reports/${report.id}`, body);
      toast.success("Saved");
      setEditing(false);
      onChanged(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      if (editing) await saveEdits();
      await api.post(`/admin/reports/${report.id}/approve`);
      toast.success("Approved & published");
      onChanged(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/reports/${report.id}/reject`, { moderation_note: rejectNote });
      toast.success("Rejected");
      onChanged(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const editInput = (path, val, rows) => rows ? (
    <textarea rows={rows} value={val || ""} onChange={(e) => set(path, e.target.value)} className={ui.input} />
  ) : (
    <input value={val ?? ""} onChange={(e) => set(path, e.target.value)} className={ui.input} />
  );

  return (
    <div className="fixed inset-0 z-[60] bg-[#210201]/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#FFFDF9] rounded-xl border border-[#E8DCC8] w-full max-w-2xl my-8 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="admin-report-detail">
        <div className="flex items-center justify-between p-5 border-b border-[#E8DCC8] sticky top-0 bg-[#FFFDF9] rounded-t-xl">
          <h3 className="font-serif text-xl font-bold text-[#8B1A1A]">Report details</h3>
          <div className="flex gap-2">
            {report.status === "pending" && (
              <button onClick={() => setEditing((e) => !e)} className={ui.btnGhost} data-testid="admin-edit-toggle-btn"><Pencil size={16} /> {editing ? "Cancel edit" : "Edit"}</button>
            )}
            <button onClick={onClose} className={ui.btnGhost}><X size={18} /></button>
          </div>
        </div>

        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div><div className="text-xs uppercase tracking-wider text-[#5A4441] mb-1">Exam date</div>{editing ? <input type="date" value={form.exam_date} onChange={(e) => set("exam_date", e.target.value)} className={ui.input} /> : formatDate(report.exam_date)}</div>
            <div><div className="text-xs uppercase tracking-wider text-[#5A4441] mb-1">City</div>{editing ? editInput("city", form.city) : report.city}</div>
            <div><div className="text-xs uppercase tracking-wider text-[#5A4441] mb-1">Country</div>{editing ? editInput("country", form.country) : report.country}</div>
            <div><div className="text-xs uppercase tracking-wider text-[#5A4441] mb-1">Centre</div>{editing ? editInput("centre_name", form.centre_name) : (report.centre_name || "Not specified")}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            {(report.section_tags || []).map((t) => <span key={t} className={ui.pill}>{SECTION_TAG_LABELS[t]}</span>)}
          </div>

          {report.email && <div className="text-sm text-[#5A4441]">Contact email (private): <span className="text-[#210201]">{report.email}</span></div>}

          {report.eo && (
            <div className="border-t border-[#E8DCC8] pt-3 space-y-2">
              <div className="font-semibold text-[#671813]">Expression orale</div>
              {report.eo.section_a?.reported && (editing ? <div><div className="text-xs text-[#5A4441]">A · topic</div>{editInput("eo.section_a.topic", form.eo.section_a.topic, 2)}</div> : <Field label="A · topic" value={report.eo.section_a.topic} />)}
              {report.eo.section_a?.reported && <Field label="A · predicted" value={report.eo.section_a.prediction_match} />}
              {report.eo.section_b?.reported && (editing ? <div><div className="text-xs text-[#5A4441]">B · topic</div>{editInput("eo.section_b.topic", form.eo.section_b.topic, 2)}</div> : <Field label="B · topic" value={report.eo.section_b.topic} />)}
              {report.eo.section_b?.reported && <Field label="B · predicted" value={report.eo.section_b.prediction_match} />}
              {editing ? <div><div className="text-xs text-[#5A4441]">Follow-up</div>{editInput("eo.followup", form.eo.followup, 2)}</div> : <Field label="Follow-up" value={report.eo.followup} />}
              <Field label="Examiner rating" value={report.eo.examiner_rating} />
              <Field label="Examiner comment" value={report.eo.examiner_comment} />
            </div>
          )}
          {report.ee && (
            <div className="border-t border-[#E8DCC8] pt-3 space-y-2">
              <div className="font-semibold text-[#671813]">Expression écrite</div>
              {report.ee.section_a?.reported && (editing ? <div><div className="text-xs text-[#5A4441]">A · prompt</div>{editInput("ee.section_a.prompt", form.ee.section_a.prompt, 2)}</div> : <Field label="A · prompt" value={report.ee.section_a.prompt} />)}
              {report.ee.section_a?.reported && <Field label="A · predicted" value={report.ee.section_a.prediction_match} />}
              {report.ee.section_b?.reported && (editing ? <div><div className="text-xs text-[#5A4441]">B · prompt</div>{editInput("ee.section_b.prompt", form.ee.section_b.prompt, 2)}</div> : <Field label="B · prompt" value={report.ee.section_b.prompt} />)}
              {report.ee.section_b?.reported && <Field label="B · predicted" value={report.ee.section_b.prediction_match} />}
            </div>
          )}
          {report.co && <div className="border-t border-[#E8DCC8] pt-3"><Field label="Compréhension orale matched" value={`${report.co.matched_count ?? "0"}${report.co.source ? " · " + report.co.source : ""}`} /></div>}
          {report.ce && <div className="border-t border-[#E8DCC8] pt-3"><Field label="Compréhension écrite matched" value={`${report.ce.matched_count ?? "0"}${report.ce.source ? " · " + report.ce.source : ""}`} /></div>}

          <div className="border-t border-[#E8DCC8] pt-3">
            {editing ? <div><div className="text-xs text-[#5A4441] mb-1">Note</div>{editInput("note", form.note, 3)}</div> : <Field label="Note" value={report.note} />}
          </div>
          <Field label="Difficulty" value={report.difficulty ? `${report.difficulty}/5` : ""} />
        </div>

        {/* actions */}
        <div className="p-5 border-t border-[#E8DCC8] space-y-3">
          {editing && (
            <button onClick={saveEdits} disabled={busy} className={ui.btnSecondary + " w-full"} data-testid="admin-save-edit-btn"><Save size={16} /> Save edits</button>
          )}
          {report.status === "pending" && (
            <>
              <div className="flex gap-3">
                <button onClick={approve} disabled={busy} className={ui.btnPrimary + " flex-1"} data-testid="admin-approve-btn"><Check size={16} /> {editing ? "Save & approve" : "Approve"}</button>
                <button onClick={reject} disabled={busy} className={ui.btnSecondary + " flex-1"} data-testid="admin-reject-btn"><X size={16} /> Reject</button>
              </div>
              <input value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Rejection reason (optional)" className={ui.input} data-testid="admin-reject-note" />
            </>
          )}
          {report.status === "rejected" && (
            <button onClick={approve} disabled={busy} className={ui.btnPrimary + " w-full"} data-testid="admin-approve-btn"><Check size={16} /> Approve instead</button>
          )}
          {report.status === "approved" && (
            <button onClick={reject} disabled={busy} className={ui.btnSecondary + " w-full"} data-testid="admin-reject-btn"><X size={16} /> Unpublish (reject)</button>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsPanel({ onClose }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!s) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-[#210201]/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FFFDF9] rounded-xl border border-[#E8DCC8] w-full max-w-md p-6 space-y-5" onClick={(e) => e.stopPropagation()} data-testid="admin-settings-panel">
        <h3 className="font-serif text-xl font-bold text-[#8B1A1A]">Archive settings</h3>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={s.gate_enabled} onChange={(e) => setS({ ...s, gate_enabled: e.target.checked })} className="w-5 h-5 accent-[#8B1A1A]" data-testid="admin-gate-toggle" />
          <span className="text-[#210201]">Gate archive older than X days (members only)</span>
        </label>
        <div>
          <label className={ui.label}>X (days)</label>
          <input type="number" min={1} value={s.gate_days} onChange={(e) => setS({ ...s, gate_days: Number(e.target.value) })} className={ui.input} data-testid="admin-gate-days" />
        </div>
        <p className="text-xs text-[#5A4441]">When enabled, reports older than {s.gate_days} days are visible only to logged-in members. Currently the archive is {s.gate_enabled ? "GATED" : "FREE for everyone"}.</p>
        <div className="flex gap-3">
          <button onClick={save} disabled={busy} className={ui.btnPrimary + " flex-1"} data-testid="admin-settings-save-btn">Save</button>
          <button onClick={onClose} className={ui.btnSecondary + " flex-1"}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, checked, login } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);

  // inline login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const loadCounts = useCallback(() => {
    api.get("/admin/stats").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/reports", { params: { status: tab, q } })
      .then((r) => setReports(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tab, q]);

  useEffect(() => {
    if (user && user.role === "admin") { loadCounts(); }
  }, [user, loadCounts]);

  useEffect(() => {
    if (user && user.role === "admin") {
      const t = setTimeout(load, 250);
      return () => clearTimeout(t);
    }
  }, [user, load]);

  const doLogin = async (e) => {
    e.preventDefault();
    try {
      const u = await login(email, password);
      if (u.role !== "admin") toast.error("This account is not an admin.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed"); }
  };

  const exportCsv = () => { window.open(`${API}/admin/export`, "_blank"); };

  if (!checked) return <div className="max-w-6xl mx-auto px-4 py-20 text-[#5A4441]">Loading…</div>;

  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-8 py-16">
        <h1 className="font-serif text-3xl font-bold text-[#8B1A1A] mb-2">Admin login</h1>
        <p className="text-sm text-[#5A4441] mb-8">Restricted area. Please log in with the admin account.</p>
        <form onSubmit={doLogin} className={ui.card + " p-6 space-y-5"} data-testid="admin-login-form">
          <div><label className={ui.label}>Email</label><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={ui.input} data-testid="admin-login-email" /></div>
          <div><label className={ui.label}>Password</label><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={ui.input} data-testid="admin-login-password" /></div>
          <button type="submit" className={ui.btnPrimary + " w-full"} data-testid="admin-login-submit">Log in</button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#8B1A1A]">Moderation</h1>
          <p className="text-sm text-[#5A4441] mt-1">Review and publish candidate reports.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/admin/topics")} className={ui.btnSecondary + " !py-2.5"} data-testid="admin-topicbank-btn">Topic bank</button>
          <button onClick={() => setShowSettings(true)} className={ui.btnSecondary + " !py-2.5"} data-testid="admin-settings-btn"><SettingsIcon size={16} /> Settings</button>
          <button onClick={exportCsv} className={ui.btnPrimary + " !py-2.5"} data-testid="admin-export-csv-btn"><Download size={16} /> Export CSV</button>
        </div>
      </div>

      {/* tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={t.testid}
            className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors flex items-center gap-2 ${tab === t.key ? "bg-[#8B1A1A] text-[#F5EAE0]" : "bg-[#FFFDF9] text-[#671813] border border-[#E8DCC8] hover:border-[#8B1A1A]"}`}>
            {t.label}
            <span className={`text-xs px-2 py-0.5 rounded-full ${tab === t.key ? "bg-[#F5EAE0]/25" : "bg-[#F3E5D8]"}`}>{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {/* search */}
      <div className="relative mb-6 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B1A1A]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search topics, notes, city…" className={ui.input + " !pl-9"} data-testid="admin-search-input" />
      </div>

      {/* list */}
      {loading ? <p className="text-[#5A4441]">Loading…</p> : reports.length === 0 ? (
        <div className={ui.card + " p-10 text-center text-[#5A4441]"}>No {tab} reports.</div>
      ) : (
        <div className="space-y-3" data-testid="admin-report-list">
          {reports.map((r) => (
            <button key={r.id} onClick={() => setSelected(r)} className={ui.card + " p-4 w-full text-left hover:border-[#8B1A1A]/50 transition-colors flex items-center justify-between gap-4"} data-testid="admin-report-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-sm font-medium text-[#210201]">{formatDate(r.exam_date)} · {r.city}</span>
                  {(r.section_tags || []).map((t) => <span key={t} className={ui.pill + " !text-[10px] !px-2 !py-0.5"}>{SECTION_TAG_LABELS[t]}</span>)}
                  {(r.eo?.section_a?.is_other || r.eo?.section_b?.is_other || r.ee?.section_a?.is_other || r.ee?.section_b?.is_other) && (
                    <span className="bg-[#F3E5D8] text-[#8B1A1A] text-[10px] font-semibold px-2 py-0.5 rounded-full border border-[#8B1A1A]" data-testid="admin-new-topic-badge">New topic</span>
                  )}
                </div>
                <p className="text-sm text-[#5A4441] truncate">
                  {r.eo?.section_a?.topic || r.ee?.section_a?.prompt || r.note || `${r.country}`}
                </p>
              </div>
              <span className="text-xs text-[#8B1A1A] shrink-0">Open →</span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <ReportDetail report={selected} onClose={() => setSelected(null)} onChanged={(updated) => {
          setSelected(updated);
          load();
          loadCounts();
        }} />
      )}
      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}
    </div>
  );
}
